import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { X, Cookie, Settings } from 'lucide-react';
import { analyticsAllowedFor, removeSessionReplay } from '~/utils/analyticsPolicy';

export function CookieConsent() {
  const { pathname } = useLocation();
  // One shared rule with the document shell: no banner, and no third-party
  // script, anywhere student or family data is on screen.
  const trackingAllowed = analyticsAllowedFor(pathname);
  const isParentFacing = !trackingAllowed;
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<{
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
  } | null>(null);

  // Client-side navigation from a marketing page into the dashboard would
  // otherwise carry an already-injected session-replay tag straight in.
  useEffect(() => {
    if (!trackingAllowed) {
      removeSessionReplay();
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', { analytics_storage: 'denied' });
      }
    }
  }, [pathname, trackingAllowed]);

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie-consent');
    if (savedConsent) {
      const parsed = JSON.parse(savedConsent);
      setConsent(parsed);
      applyConsent(parsed);
    } else if (!isParentFacing) {
      setShowBanner(true);
    }
  }, [isParentFacing]);

  const applyConsent = (consentData: { necessary: boolean; analytics: boolean; marketing: boolean }) => {
    // Consent lives in localStorage, so an admin who accepted on the marketing
    // site arrives here already consented. Consent is not permission to record
    // a page showing other people's children.
    if (consentData.analytics && analyticsAllowedFor(window.location.pathname)) {
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          analytics_storage: 'granted'
        });
      }

      if (typeof window !== 'undefined' && !document.querySelector('script[src*="contentsquare.net"]')) {
        const script = document.createElement('script');
        script.src = 'https://t.contentsquare.net/uxa/cb538b9560ed3.js';
        script.async = true;
        document.head.appendChild(script);
      }
    } else {
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          analytics_storage: 'denied'
        });
      }

      if (typeof window !== 'undefined') {
        const contentSquareScript = document.querySelector('script[src*="contentsquare.net"]');
        if (contentSquareScript) {
          contentSquareScript.remove();
        }
      }
    }
  };

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: false
    };
    setConsent(consentData);
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    applyConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setConsent(consentData);
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    applyConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    if (consent) {
      localStorage.setItem('cookie-consent', JSON.stringify(consent));
      localStorage.setItem('cookie-consent-date', new Date().toISOString());
      applyConsent(consent);
      setShowBanner(false);
      setShowSettings(false);
    }
  };

  const handleCustomize = () => {
    setShowSettings(true);
    if (!consent) {
      setConsent({
        necessary: true,
        analytics: false,
        marketing: false
      });
    }
  };

  if (isParentFacing || (!showBanner && !showSettings)) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-[var(--color-hairline)] bg-[var(--color-paper)] shadow-[var(--shadow-2)]">
      <div className="mx-auto max-w-[1040px] px-4 py-4 sm:px-6">
        {!showSettings ? (
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--color-board)]" />
              <div className="flex-1">
                <p className="text-sm text-[var(--color-ink)]">
                  We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.
                  By clicking &quot;Accept All&quot;, you consent to our use of cookies. You can also{' '}
                  <button
                    onClick={handleCustomize}
                    className="font-semibold text-[var(--color-board)] underline"
                  >
                    customize your preferences
                  </button>
                  {' '}or{' '}
                  <Link to="/privacy" className="font-semibold text-[var(--color-board)] underline">
                    learn more
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-3">
              <button
                onClick={handleRejectAll}
                className="rounded-[var(--radius-sm)] border border-[var(--color-hairline)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-ink)] hover:bg-[var(--color-panel)]"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="rounded-[var(--radius-sm)] bg-[var(--color-board)] px-4 py-2 text-sm font-bold text-[var(--color-paper)] hover:bg-[var(--accent-dark)]"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-[var(--color-board)]" />
                <h3 className="text-lg font-extrabold text-[var(--color-ink)]">Cookie Preferences</h3>
              </div>
              <button
                onClick={() => {
                  setShowSettings(false);
                  if (!consent) {
                    setShowBanner(true);
                  }
                }}
                className="p-1 text-[var(--color-ash)] hover:text-[var(--color-ink)]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-[var(--radius-md)] border border-[var(--color-hairline)] bg-white p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-[var(--color-ink)]">Necessary Cookies</h4>
                    <p className="text-sm text-[var(--color-ash)]">Required for the site to function properly</p>
                  </div>
                  <span className="scg-mono text-[0.72rem] text-[var(--color-board)]">Always Active</span>
                </div>
                <p className="mt-2 text-xs text-[var(--color-ash)]">
                  These cookies are essential for authentication, security, and core functionality. They cannot be disabled.
                </p>
              </div>

              <div className="rounded-[var(--radius-md)] border border-[var(--color-hairline)] bg-white p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-[var(--color-ink)]">Analytics Cookies</h4>
                    <p className="text-sm text-[var(--color-ash)]">Help us understand how visitors interact with our site</p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={consent?.analytics || false}
                      onChange={(e) => {
                        if (consent) {
                          setConsent({ ...consent, analytics: e.target.checked });
                        }
                      }}
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-[var(--color-panel)] after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-[var(--color-hairline)] after:bg-white after:transition-all after:content-[''] peer-checked:bg-[var(--color-board)] peer-checked:after:translate-x-full peer-focus:outline-none"></div>
                  </label>
                </div>
                <p className="mt-2 text-xs text-[var(--color-ash)]">
                  We use Google Analytics and ContentSquare to analyze site usage and improve user experience.
                </p>
              </div>

              <div className="rounded-[var(--radius-md)] border border-[var(--color-hairline)] bg-white p-4 opacity-60">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-[var(--color-ink)]">Marketing Cookies</h4>
                    <p className="text-sm text-[var(--color-ash)]">Used to deliver personalized advertisements</p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={consent?.marketing || false}
                      disabled
                      className="peer sr-only"
                    />
                    <div className="h-6 w-11 rounded-full bg-[var(--color-panel)] after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-[var(--color-hairline)] after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full"></div>
                  </label>
                </div>
                <p className="mt-2 text-xs text-[var(--color-ash)]">
                  Currently not in use. This option is available for future marketing features.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-[var(--color-hairline)] pt-4">
              <Link to="/privacy" className="text-sm font-semibold text-[var(--color-board)] underline">
                View Privacy Policy
              </Link>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowSettings(false);
                    if (!consent) {
                      setShowBanner(true);
                    }
                  }}
                  className="rounded-[var(--radius-sm)] border border-[var(--color-hairline)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-ink)] hover:bg-[var(--color-panel)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="rounded-[var(--radius-sm)] bg-[var(--color-board)] px-4 py-2 text-sm font-bold text-[var(--color-paper)] hover:bg-[var(--accent-dark)]"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
